import { useCallback, useRef } from "react";
import { TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";

interface Props {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  format?: (n: number) => string;
}

// Brutalist two-thumb range slider. Pure CSS/pointer math, no deps.
export function BrRangeSlider({ min, max, step = 1, value, onChange, format }: Props) {
  const p = useTheme();
  const trackRef = useRef<HTMLDivElement>(null);
  const [lo, hi] = value;
  const span = Math.max(1, max - min);
  const pct = (n: number) => ((n - min) / span) * 100;

  const fmt = format ?? ((n: number) => String(n));

  const valueFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return min;
      const rect = el.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const raw = min + ratio * span;
      return Math.round(raw / step) * step;
    },
    [min, span, step],
  );

  const startDrag = (thumb: "lo" | "hi") => (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const move = (ev: PointerEvent) => {
      const v = valueFromClientX(ev.clientX);
      if (thumb === "lo") onChange([Math.min(v, hi), hi]);
      else onChange([lo, Math.max(v, lo)]);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const thumbStyle = (left: number): React.CSSProperties => ({
    position: "absolute",
    top: "50%",
    left: `${left}%`,
    transform: "translate(-50%, -50%)",
    width: 16,
    height: 16,
    background: p.accent,
    border: `2px solid ${p.border}`,
    cursor: "pointer",
    touchAction: "none",
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          fontFamily: TYPE.mono,
          fontWeight: 800,
          marginBottom: 6,
        }}
      >
        <span>{fmt(lo)}</span>
        <span>{fmt(hi)}</span>
      </div>
      <div
        ref={trackRef}
        style={{
          position: "relative",
          height: 16,
          background: p.bg,
          border: `2px solid ${p.border}`,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${pct(lo)}%`,
            width: `${pct(hi) - pct(lo)}%`,
            background: p.accent,
            opacity: 0.5,
          }}
        />
        <div onPointerDown={startDrag("lo")} style={thumbStyle(pct(lo))} />
        <div onPointerDown={startDrag("hi")} style={thumbStyle(pct(hi))} />
      </div>
    </div>
  );
}
